<nav aria-label="breadcrumb">
  <ol class="breadcrumb rounded p-3">
    <li class="breadcrumb-item"> <a routerLinkActive="active" [routerLink]="['../']"> LLM Playground </a> </li>
    <li class="breadcrumb-item active" aria-current="page">
      {{ project?.name }}
    </li>

    @if (viewingBatchResults) {
      <li class="ms-auto">
        Viewing prompt for batch {{currentBatchName}}
        <button class="btn btn-sm btn-secondary ms-2" (click)="onCopyBatchPrompt()">Copy To Project</button>
        <button class="btn btn-sm btn-info ms-2" (click)="onPromptBack()">Back</button>
      </li>
    }

    @if (!viewingBatchResults) {
      <li class="ms-auto">
        @if (saveError) {
          <span class="text-danger">{{saveError}}</span>
        }
        @if (isOwner) {
          <a href="#" class="me-2" (click)="onDeleteProject($event)">Delete</a>
        }
        <a href="#" class="btn btn-sm btn-primary" (click)="onShareProject($event)">Share</a>
        @if (isSaving) {
          <mat-spinner class="me-2 inline-spinner" [diameter]="14" />
        }
        <button class="btn btn-sm btn-info" type="button" (click)="onPromptSave()" [disabled]="responseLoading">
          Save
        </button>
      </li>
    }
  </ol>
</nav>

@if (promptForm) {
  <form [formGroup]="promptForm" (ngSubmit)="onPromptSave()">
    <div class="row g-0 mb-4 text-start">
      <div class="col-3">
        <div class="mb-3 me-2">
          <label class="my-0"><strong>Model</strong></label>
          @if (model && !viewingBatchResults) {
            <select class="form-select" formControlName="model">
              <option value="auto">Auto</option>
              @for (modelOption of models; track modelOption) {
                <option [value]="modelOption.name"> {{ modelOption.displayName }} </option>
              }
            </select>
          }
          @if (viewingBatchResults) {
            <input readonly class="form-control" [value]="modelName">
          }
        </div>
      </div>
      <div class="col-3 px-1">
        <label class="my-0"><strong>Use JSON mode</strong></label>
        <div class="form-check form-switch">
          <input type="checkbox" role="switch" class="form-check-input" [value]="project.prompt.useJsonMode" formControlName="useJsonMode">
        </div>
      </div>
      <div class="col-3">
        <div class="mb-3 me-2">
          <label class="my-0"><strong>Temperature</strong></label>
          <input [readonly]="viewingBatchResults" class="form-control" type="number" id="temperature" step="0.01" min="0" max="2" pattern="^\d+(\.\d{1,2})?$" formControlName="temperature" required>
          @if (temperature.touched && temperature.errors?.required) {
            <div class="text-danger">
              Temperature is required.
            </div>
          }
          @if (temperature.touched && (temperature.errors?.min || temperature.errors?.max)) {
            <div class="text-danger">
              Temperature must be between 0.00 and 2.00 (inclusive).
            </div>
          }
        </div>
      </div>
      <div class="col-3">
        <label class="my-0"><strong>Tokens Used</strong></label>
        <input type="text" class="form-control" readonly value="{{totalTokens}}">
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-md-6">
        <label>System Message</label>
        <textarea
          [readonly]="viewingBatchResults"
          id="systemMessage"
          formControlName="systemMessage"
          class="form-control"
          rows="22"
        ></textarea>
        <div formArrayName="examples">
          <div class="mt-3 mb-2">
            <h6>Examples</h6>
          </div>
          @for (example of examples.controls; track example; let i = $index) {
            <ng-container [formGroupName]="i">
              @if (!example.value._destroy) {
                @if (shouldDisplayHr(i)) {
                  <hr>
                  }
                  <div class="d-flex justify-content-between align-items-center">
                    <label for="userMessage">User</label>
                    @if (!viewingBatchResults && shouldDisplayExampleDelete) {
                      <a href="#" (click)="deleteExample($event, i)" class="ms-auto">Delete</a>
                    }
                  </div>
                  <textarea
                    (input)="onExampleChange()"
                    [readonly]="viewingBatchResults"
                    formControlName="userMessage"
                    class="form-control mb-2"
                    rows="9"
                  ></textarea>
                  <label for="assistantMessage">Assistant</label>
                  <textarea
                    (input)="onExampleChange()"
                    [readonly]="viewingBatchResults"
                    formControlName="assistantMessage"
                    class="mb-2 form-control"
                    rows="9"
                  ></textarea>
                }
              </ng-container>
            }
            @if (!viewingBatchResults) {
              <button type="button" class="btn btn-sm btn-info" (click)="addExample()">Add Example</button>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="mb-2 d-flex justify-content-between align-items-center">
            <label>Chat</label>
            @if (allChatsUsedTheSameModel) {
              <div class="model-gray">{{chats[0].model}}</div>
            }
          </div>
          @if (responseLoading || chats.length > 0) {
            <div #chatContainer class="chat-container border">
              @for (chat of chats; track chat) {
                <div>
                  <div class="row g-0 mb-1">
                    <div class="col-11 me-auto chat-balloon scrollable-div u-100 p-2 ms-3 mt-2 border rounded bg-light">
                      <pre class="text-pre-wrap">{{chat.userMessage}}</pre>
                    </div>
                  </div>
                  @if (chat.assistantMessage) {
                    <div class="row g-0 mb-1">
                      <div class="col-11 me-auto chat-balloon chat-assistant scrollable-div u-100 p-2 ms-3 mt-2 border rounded">
                        @if (!allChatsUsedTheSameModel) {
                          <div class="model-gray right-align">{{chat.model}}</div>
                        }
                        <pre [ngClass]="{'text-danger': chat.status === 'failed'}" class="text-pre-wrap">{{chat.assistantMessage || "error"}}</pre>
                        @if (chat.finishReason === 'length') {
                          <div class="text-danger">
                            Truncated due to Max Tokens Per Response setting
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
              @if (responseLoading) {
                <div class="m-2 d-flex justify-content-center">
                  <mat-spinner [diameter]="28" />
                </div>
              }
            </div>
          }
          <textarea
            [readonly]="viewingBatchResults"
            (keydown.enter)="onEnterSubmit($event)"
            formControlName="userMessage"
            class="mt-4 form-control"
            rows="9"
          ></textarea>
          <div class="mt-2 d-flex justify-content-between align-items-center">
            <div>
              @if (promptForm.errors?.atLeastOnePromptFieldRequired) {
                <div class="text-danger">
                  At least one prompt field is required.
                </div>
              }
              @if (responseError.length > 0) {
                <div class="text-danger text-end">
                  {{ responseError }}
                </div>
              }
            </div>
            <div>
              @if (chats.length > 0 && !viewingBatchResults) {
                <button type="button" class="btn btn-sm btn-info" [disabled]="disableAutomatedSubmission" (click)="onResubmitFirst()">Resubmit First</button>
                <button type="button" class="btn btn-sm btn-info" (click)="onClearChat()">Clear</button>
              }
              @if (!viewingBatchResults) {
                <button class="btn btn-sm btn-primary" type="button" (click)="onPromptSave(true)" [disabled]="promptForm.invalid || responseLoading">Send</button>
              }
            </div>
          </div>
        </div>
      </div>
    </form>
  }

  <hr/>

  <div class="row ">
    <div class="col-8 text-start">
      <h3>Batch Runs</h3>
    </div>
    @if (promptForm) {
      <div class="col-4 text-end">
        @if (!viewingBatchResults) {
          <button [disabled]="disableAutomatedSubmission" class="btn btn-sm btn-primary" (click)="onOpenAddBatchModal()">Save & Add Batch Run</button>
        }
      </div>
    }

    @if (batches?.length > 0) {
      <div class="col-12">
        <app-llm-playground-batches-table
          [batches]="batches"
          (view)="onViewBatchPrompt($event)"
          (delete)="onDeleteBatchRun($event)"
          />
      </div>
    } @else {
      <section class="col-12"><p>There are no batch runs.</p></section>
    }
  </div>
